import { CanvasLayoutPositions } from "@common/schemas/domains/accounting/consolidation.schema";
import {
    ENTITY_NODE_HEIGHT,
    ENTITY_NODE_WIDTH,
    OwnershipFlowNode,
} from "./ownership.types";
import { getAbsoluteNodeRects, NodeRect } from "./ownershipEdgeRouting.helpers";
import { overlapsWithGap } from "./ownershipHelperLines.helpers";

export type SavedPositions = CanvasLayoutPositions;

interface DragMove {
    id: string;
    position: { x: number; y: number };
}

const NUDGE_GAP = 20;
const NUDGE_STEP = ENTITY_NODE_HEIGHT + NUDGE_GAP;
const MAX_NUDGE_STEPS = 40;

/**
 * Every entity and group node carries a saved position; the synthetic group
 * header is derived from its group each render, so it never does. A grouped
 * child's position is stored in its group-relative frame (see the box-bounds
 * guard in `applySavedPositions`, which rejects a stale absolute value left
 * over from before the node was consolidated).
 */
function isPersistedNode(node: OwnershipFlowNode): boolean {
    return node.type === "entityNode" || node.type === "groupNode";
}

/**
 * A grouped child's saved position is valid only if it lands inside its group
 * box. A value outside the box is a stale absolute coordinate from before the
 * node was consolidated — reject it so ELK re-arranges the member instead of
 * piling it onto the group origin.
 */
function fitsInsideParentBox(
    position: { x: number; y: number },
    node: OwnershipFlowNode,
    parent: OwnershipFlowNode,
): boolean {
    const width = node.width ?? ENTITY_NODE_WIDTH;
    const height = node.height ?? ENTITY_NODE_HEIGHT;
    const parentWidth = parent.width ?? 0;
    const parentHeight = parent.height ?? 0;
    return (
        position.x >= 0 &&
        position.y >= 0 &&
        position.x + width <= parentWidth &&
        position.y + height <= parentHeight
    );
}

/**
 * Substitutes saved positions into ELK's output. `saved === null` means the
 * layout was never frozen — ELK drives everything and the input array is
 * returned by reference. Group width/height always stay ELK-derived so a group
 * box keeps growing to hold new members.
 */
export function applySavedPositions({
    layoutedNodes,
    saved,
}: {
    layoutedNodes: OwnershipFlowNode[];
    saved: SavedPositions | null;
}): OwnershipFlowNode[] {
    if (saved === null) {
        return layoutedNodes;
    }
    const byId = new Map(layoutedNodes.map((node) => [node.id, node]));
    return layoutedNodes.map((node) => {
        const position = saved[node.id];
        if (!position || !isPersistedNode(node)) {
            return node;
        }
        // A grouped child keeps its saved (relative) spot only if it still fits
        // the box; a stale absolute value falls back to ELK's arrangement.
        if (node.parentId) {
            const parent = byId.get(node.parentId);
            if (parent && !fitsInsideParentBox(position, node, parent)) {
                return node;
            }
        }
        return { ...node, position: { x: position.x, y: position.y } };
    });
}

/**
 * Pre-expansion snapshot of every persisted node, in ELK's coordinate space
 * (entity children relative to their group parent; roots and groups absolute).
 */
export function captureLayoutPositions(
    layoutedNodes: OwnershipFlowNode[],
): SavedPositions {
    const positions: SavedPositions = {};
    for (const node of layoutedNodes) {
        if (isPersistedNode(node)) {
            positions[node.id] = {
                x: node.position.x,
                y: node.position.y,
            };
        }
    }
    return positions;
}

/** Node ids present in `layoutedNodes` but absent from a frozen `saved` map. */
export function unsavedIdsOf(
    layoutedNodes: OwnershipFlowNode[],
    saved: SavedPositions | null,
): Set<string> {
    if (saved === null) {
        return new Set();
    }
    const unsaved = new Set<string>();
    for (const node of layoutedNodes) {
        if (isPersistedNode(node) && saved[node.id] === undefined) {
            unsaved.add(node.id);
        }
    }
    return unsaved;
}

function fitsInsideParent(rect: NodeRect, parent: NodeRect): boolean {
    return (
        rect.x >= parent.x &&
        rect.y >= parent.y &&
        rect.x + rect.width <= parent.x + parent.width &&
        rect.y + rect.height <= parent.y + parent.height
    );
}

/**
 * Pushes newly-appeared nodes (present in the graph but with no saved position)
 * down out of collision with the anchored, saved nodes. Deterministic: floating
 * nodes are placed in `(y, x, id)` order, each dropping straight down in
 * `NUDGE_STEP` increments until clear. Grouped nodes that cannot be nudged
 * inside their parent box keep their ELK slot (which is free by construction,
 * since ELK sized the box for exactly the current membership). The nudge is
 * never persisted — it relaxes back once the colliding node is dragged or
 * removed. Only entity nodes participate; group boxes are not obstacles.
 */
export function nudgeUnsavedNodes({
    nodes,
    unsavedIds,
}: {
    nodes: OwnershipFlowNode[];
    unsavedIds: ReadonlySet<string>;
}): OwnershipFlowNode[] {
    if (unsavedIds.size === 0) {
        return nodes;
    }

    const rects = getAbsoluteNodeRects(nodes);

    const floating = nodes.filter(
        (node) => node.type === "entityNode" && unsavedIds.has(node.id),
    );
    if (floating.length === 0) {
        return nodes;
    }

    const placedRects: NodeRect[] = nodes
        .filter(
            (node) => node.type === "entityNode" && !unsavedIds.has(node.id),
        )
        .flatMap((node) => {
            const rect = rects.get(node.id);
            return rect ? [rect] : [];
        });

    floating.sort((a, b) => {
        const rectA = rects.get(a.id);
        const rectB = rects.get(b.id);
        if (!rectA || !rectB) {
            return a.id.localeCompare(b.id);
        }
        return (
            rectA.y - rectB.y || rectA.x - rectB.x || a.id.localeCompare(b.id)
        );
    });

    const overrides = new Map<string, { x: number; y: number }>();
    for (const node of floating) {
        const rect = rects.get(node.id);
        if (!rect) {
            continue;
        }
        const parentRect = node.parentId ? rects.get(node.parentId) : undefined;
        let candidate = rect;
        let steps = 0;
        while (
            steps < MAX_NUDGE_STEPS &&
            placedRects.some((placed) =>
                overlapsWithGap(candidate, placed, NUDGE_GAP),
            )
        ) {
            candidate = { ...candidate, y: candidate.y + NUDGE_STEP };
            steps += 1;
        }
        // A grouped node that cannot be moved down and still fit inside its box
        // keeps its original ELK slot rather than escaping the boundary.
        if (parentRect && !fitsInsideParent(candidate, parentRect)) {
            placedRects.push(rect);
            continue;
        }
        placedRects.push(candidate);
        if (candidate.y !== rect.y) {
            overrides.set(node.id, {
                x: node.position.x,
                y: node.position.y + (candidate.y - rect.y),
            });
        }
    }

    if (overrides.size === 0) {
        return nodes;
    }
    return nodes.map((node) => {
        const override = overrides.get(node.id);
        return override ? { ...node, position: override } : node;
    });
}

/**
 * The drag-stop writer. Given the pre-expansion nodes the render was built from
 * and the post-expansion nodes React Flow actually rendered (and reported drags
 * against), produces the next saved map.
 *
 * Because `expandGroupNodesForEdgeContent` is a pure per-node translation and
 * the rendered array is frozen for the whole gesture, the expansion offset is
 * constant across `rendered_new − rendered_old` and cancels — so applying that
 * delta to the pre-expansion position needs no knowledge of the offset itself.
 *
 * The first drag (`saved === null`) freezes the full current layout; every
 * later write merges deltas onto the existing map so positions for
 * filter-hidden nodes are never dropped.
 */
export function nextSavedPositions({
    saved,
    positionedNodes,
    renderedNodes,
    drags,
}: {
    saved: SavedPositions | null;
    positionedNodes: OwnershipFlowNode[];
    renderedNodes: OwnershipFlowNode[];
    drags: readonly DragMove[];
}): SavedPositions {
    const preNodeById = new Map(positionedNodes.map((node) => [node.id, node]));
    const renderedById = new Map(
        renderedNodes.map((node) => [node.id, node.position]),
    );

    const base: SavedPositions =
        saved === null ? captureLayoutPositions(positionedNodes) : { ...saved };

    for (const drag of drags) {
        const node = preNodeById.get(drag.id);
        const renderedOld = renderedById.get(drag.id);
        // Skip non-persisted nodes (the header) and any drag with no matching
        // rendered node (can't be inverted safely).
        if (!node || !renderedOld || !isPersistedNode(node)) {
            continue;
        }
        base[drag.id] = {
            x: node.position.x + (drag.position.x - renderedOld.x),
            y: node.position.y + (drag.position.y - renderedOld.y),
        };
    }

    return base;
}
